import { ChangeEvent, useEffect, useState } from 'react';
import { Button } from 'common/components';
import { Accordion } from 'components/Accordion';
import { EffectiveTransferExperience } from 'services/api/WorkflowServiceAPI';
// File paths rather than the barrels: the barrels load every API module and
// the shared axios instance, which the test mocks do not cover.
import { useGetWorkflowConfig } from 'services/hooks/queries/useGetWorkflowConfig';
import { useGetTransferAudio } from 'services/hooks/queries/useGetTransferAudio';
import { useDeleteTransferAudio } from 'services/hooks/mutations/useDeleteTransferAudio';
import { useUploadTransferAudio } from 'services/hooks/mutations/useUploadTransferAudio';
import {
    TRANSFER_AUDIO_ACCEPT,
    transferAudioFileName,
    validateTransferAudioFile,
} from 'pages/WorkflowBuilder/transferAudioUtils';

const DEFAULT_TRANSFER_EXPERIENCE: EffectiveTransferExperience = { holdAudio: 'ringtone', source: 'none' };

const serverErrorMessage = (error: unknown, fallback: string): string => {
    const message = (error as { response?: { data?: { error?: unknown } } } | null)?.response?.data?.error;
    return typeof message === 'string' && message ? message : fallback;
};

export const TransferAudioSection = ({ workflowId }: { workflowId: string }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);

    const { workflowConfig } = useGetWorkflowConfig(workflowId);
    const { holdAudio, holdAudioKey } = workflowConfig?.effectiveTransferExperience ?? DEFAULT_TRANSFER_EXPERIENCE;
    const hasCustomClip = holdAudio === 'custom';

    // The clip only exists once holdAudio is custom; a request before that is a
    // guaranteed 404. Gated on the section being open too, so visiting the builder
    // does not download up to 2 MB nobody asked to hear.
    const { transferAudio, isError: isPreviewError } = useGetTransferAudio(workflowId, hasCustomClip && isOpen);
    // Ignore a blob left over from a clip that has since been removed.
    const previewBlob = hasCustomClip ? transferAudio : undefined;
    const { uploadTransferAudio, isPending: isUploading } = useUploadTransferAudio();
    const { deleteTransferAudio, isPending: isRemoving } = useDeleteTransferAudio();
    const isBusy = isUploading || isRemoving;

    useEffect(() => {
        if (!previewBlob) {
            setPreviewUrl(null);
            return;
        }
        const url = URL.createObjectURL(previewBlob);
        setPreviewUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [previewBlob]);

    const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
        const input = event.target;
        const file = input.files?.[0];
        setError(null);
        if (!file) {
            return;
        }
        const validationError = validateTransferAudioFile(file);
        if (validationError) {
            setError(validationError);
            // Clearing lets the user pick the same file again after fixing it.
            input.value = '';
            return;
        }
        try {
            await uploadTransferAudio({ id: workflowId, file });
        } catch (err) {
            setError(serverErrorMessage(err, 'Could not upload the clip. Please try again.'));
        } finally {
            input.value = '';
        }
    };

    const handleRemove = async () => {
        setError(null);
        try {
            await deleteTransferAudio(workflowId);
        } catch (err) {
            setError(serverErrorMessage(err, 'Could not remove the clip. Please try again.'));
        }
    };

    return (
        <Accordion isOpen={isOpen} onToggle={() => setIsOpen((open) => !open)} header='Transfer audio'>
            <p className='text-sm text-secondary mt-md'>
                The clip plays once when a transfer starts. It replaces the default ringing tone, and it is cut short if
                an agent answers first.
            </p>

            {hasCustomClip ? (
                <div className='mt-md'>
                    <p>{transferAudioFileName(holdAudioKey)}</p>
                    {previewUrl && <audio className='mt-md' controls src={previewUrl} />}
                    {isPreviewError && <p className='text-sm text-secondary mt-md'>Preview unavailable.</p>}
                </div>
            ) : (
                <p className='mt-md'>The default ringing tone is in use.</p>
            )}

            <div className='flex items-center gap-sm mt-md'>
                <label>
                    {hasCustomClip ? 'Replace clip' : 'Upload a clip (WAV or MP3, up to 2 MB)'}
                    <input type='file' accept={TRANSFER_AUDIO_ACCEPT} disabled={isBusy} onChange={handleFileChange} />
                </label>
                {hasCustomClip && (
                    <Button variant='secondary' onClick={handleRemove} disabled={isBusy}>
                        {isRemoving ? 'Removing...' : 'Remove'}
                    </Button>
                )}
            </div>

            {isUploading && <p className='text-sm text-secondary mt-md'>Uploading...</p>}
            {error && (
                <p role='alert' className='text-sm text-error mt-md'>
                    {error}
                </p>
            )}
        </Accordion>
    );
};
